import { ExerciseType, GameRating } from '../api/gameTypes'
import {
  CompareGamesResponse,
  DailyPlayers,
  InstructorReportResponse,
  OrgReportResponse,
  SponsoredReportApiGame,
  SponsoredReportSummaryResponse,
  PlayerReportDataApiResponse,
} from '../api/reportingTypes'
import { Badge, DateTime, Task } from '../types/commonTypes'

export type OrgReportUserData = {
  rowType: ReportType.ORG
  userName: string
  numberOfGamesCreated: number
  numberOfGamesCoOwned: number
  numberOfGamesPlayed: number
  numberOfGamesCoPlayed: number
  numberOfPlayersInGames: number
}

export type OrgReportSummaryData = {
  noOfUsers: number
  noOfGames: number
  noOfPlayedGames: number
  noOfPlayersInGames: number
  fromDate?: DateTime
  untilDate?: DateTime
  noOfTasks: number
  noOfAnswers: number
  pointsCount: number
}

export type InstructorReportGameData = {
  rowType: ReportType.INSTRUCTOR
  name: string
  id: number
  createdAt: DateTime
  creator: string
  openStatus: boolean
  numberOfExercises: number
  numberOfAnswers: number
  numberOfTeams: number
  firstAnswerReceived: DateTime
  lastAnswerReceived: DateTime
  thumbnailUrl: string
}

export type InstructorReportTopGame = {
  id: number
  gameName: string
  playerInfo: number
  modifiedAt: DateTime | string
  thumbnailUrl: string
}

export type InstructorReportSummaryData = {
  totalGames: number
  totalPlayed: number
  totalExercises: number
  totalAnswers: number
  totalTeams: number
  fromDate: DateTime
  untilDate: DateTime
  topGames: InstructorReportTopGame[]
}

export type CompareGameData = {
  id: number
  name: string
  thumbnailUrl: string
  updatedAt: DateTime | string
  createdAt: DateTime | string
  avgTime: number
  playersStartedCount: number
  playersTotalCount: number
  playersCompletedCount: number
  completionRate: number
}

export type SponsoredReportGame = {
  conlibGameId: number
  conlibGame?: any
  gameName: string
  thumbnailUrl: string
  createdAt?: DateTime
  updatedAt?: DateTime
  creator: string
  ratingDetails: GameRating
  numberOfDownloadedGames: number
  numberOfGamesPlayed: number
  numberOfPlayers: number
  userDownloads?: SponsoredDownloader[]
  userDownloadsTotalCount?: number
  downloadsByDays?: DailyPlayers
}

export type SponsoredDownloader = {
  rowType: ReportType.SPONSORED
  email: string
  createdAt: DateTime
  organisation: string
}

export type SponsoredReportSummaryData = {
  totalGames: number
  fromDate: DateTime
  untilDate: DateTime
}

export type SponsoredReportData = {
  summary: SponsoredReportSummaryData
  games: SponsoredReportGame[]
}

export enum ReportType {
  'ORG',
  'INSTRUCTOR',
  'SPONSORED',
  'PLAYER',
}

export type GenericReportPageData = OrgReportUserData | InstructorReportGameData | SponsoredDownloader

export type BadgeInfo = {
  id: number
  name: string
  imageUrl: string
  count: number
  userIds: number[]
}

export type PlayerDataRow = {
  rank: number //?
  nickName: string
  email?: string
  teamMemberNames?: string
  points: number
  correctPercentage: number
  gameDuration: number
  completionRate: number
  awardedBadges: Badge[]
  overallCompleted: boolean
  createdAt: string
  completedAt?: string
}

export type GameReportSummaryData = {
  gameName: string
  completionRate: number
  averageScore: number
  playerCount: number
  mostDifficultTask: {
    name: string
    correctPercentage: number
    averageTime: number
  }
  mostSuccesfulTask: {
    name: string
    correctPercentage: number
    averageTime: number
  }
  playersNotFinished: string[]
  awardedBadges: BadgeInfo[]
}

export type TaskDataRow = {
  task: Task
  id: number
  name: string
  type: string
  rawType: ExerciseType
  answerCount: number
  maxPoints: number
  completedCount: number
  evaluatedCount: number
  averagePoints: number
  averageScorePercentage: number
  averageTime: number
  lastActionTime: string
}

export type PlayerReportPlayerInfo = {
  playerEmail: string
  teamName: string
  teamPoints: number
  completedExercises: number
  gameCompleted: boolean | string
  lastAnswerDate: string
  badgeCount: number
}

export type PlayerReportGame = {
  gameId: number
  gameName: string
  maxPoints: number
  exerciseCount: number
  playerCount: number
  open: boolean | string
  coverPictureUrl?: string
  mapUrl: string
  gamerAccounts: PlayerReportPlayerInfo[]
  avgTime: string
  playersCompleted: number
  lastAnswerDate: string
}

export type PlayerReportData = {
  allPlayerAccounts: string[]
  reportData: PlayerReportGame[]
}

export const parseSponsoredReportGameData = (game: SponsoredReportApiGame): SponsoredReportGame => {
  return {
    conlibGameId: game.conlib_game_id,
    gameName: game.name,
    thumbnailUrl: game.thumbnail_url,
    createdAt: game.created_at_raw,
    creator: game.creator,
    ...(game.rating_details && { ratingDetails: game.rating_details[0] }),
    numberOfDownloadedGames: game.number_of_games,
    numberOfGamesPlayed: game.number_of_games_played,
    numberOfPlayers: game.number_of_players,
    updatedAt: game.updated_at_raw,
    userDownloadsTotalCount: game.user_downloads_count,
    userDownloads: game.user_downloads
      ? (game.user_downloads.map((d) => {
          return {
            rowType: ReportType.SPONSORED,
            email: d.email,
            createdAt: d.created_at,
            organisation: d.organisation,
          }
        }) as SponsoredDownloader[])
      : undefined,
    downloadsByDays: game.downloads_by_days,
  }
}

export const parseSponsoredReportData = (data: SponsoredReportSummaryResponse): SponsoredReportData => {
  const { summary, games } = data
  return {
    summary: { totalGames: summary.total_games, fromDate: summary.from_date, untilDate: summary.until_date },
    games: games.map((game) => {
      return {
        conlibGameId: game.conlib_game_id,
        gameName: game.name,
        thumbnailUrl: game.thumbnail_url,
        createdAt: game.created_at_raw,
        creator: game.creator,
        ratingDetails: game.rating_details[0],
        numberOfDownloadedGames: game.number_of_games,
        numberOfGamesPlayed: game.number_of_games_played,
        numberOfPlayers: game.number_of_players,
        updatedAt: game.updated_at_raw,
        userDownloads: game.user_downloads
          ? (game.user_downloads.map((d) => {
              return {
                rowType: ReportType.SPONSORED,
                email: d.email,
                createdAt: d.created_at,
                organisation: d.organisation,
              }
            }) as SponsoredDownloader[])
          : undefined,
        downloadsByDays: game.downloads_by_days,
      }
    }),
  }
}

export const parseCompareGameApiData = (data: CompareGamesResponse): CompareGameData[] => {
  return data.compare_data.map((item) => {
    return {
      id: item.id,
      name: item.name,
      thumbnailUrl: item.thumbnail_url,
      createdAt: new Date(item.created_at + '').toLocaleDateString(),
      updatedAt: new Date(item.updated_at + '').toLocaleDateString(),
      avgTime: item.avg_time,
      playersStartedCount: item.players_started,
      playersCompletedCount: item.players_completed_count,
      playersTotalCount: item.players_total_count,
      completionRate: item.completion_rate,
    }
  })
}

export const parseOrgSummaryApiData = (
  data: OrgReportResponse,
): { usersTable: OrgReportUserData[]; summaryData: OrgReportSummaryData } => {
  const { summary, userCollection } = data
  return {
    usersTable: userCollection.map((user) => {
      return {
        rowType: ReportType.ORG,
        userName: user.user_name,
        numberOfGamesCoOwned: user.number_of_games_co_owned,
        numberOfGamesCoPlayed: user.number_of_games_co_played,
        numberOfGamesCreated: user.number_of_games_created,
        numberOfGamesPlayed: user.number_of_games_played,
        numberOfPlayersInGames: user.number_of_players_in_games,
      }
    }),
    summaryData: {
      noOfGames: summary.no_of_games,
      noOfPlayedGames: summary.no_of_played_games,
      noOfPlayersInGames: summary.no_of_players_in_games,
      noOfUsers: summary.no_of_users,
      fromDate: summary.from_date,
      untilDate: summary.until_date,
      noOfAnswers: summary.no_of_answers,
      noOfTasks: summary.no_of_tasks,
      pointsCount: summary.no_of_points,
    },
  }
}

export const parseInstructorSummaryApiData = (
  data: InstructorReportResponse,
): { gamesTable: InstructorReportGameData[]; summaryData: InstructorReportSummaryData } => {
  const { summary, gamesCollection } = data
  return {
    gamesTable: gamesCollection.map((game) => {
      return {
        rowType: ReportType.INSTRUCTOR,
        name: game.name,
        id: game.id,
        createdAt: game.created_at,
        creator: game.creator,
        openStatus: !!game.open_status,
        numberOfExercises: game.number_of_exercises,
        numberOfAnswers: game.number_of_answers,
        numberOfTeams: game.number_of_teams,
        firstAnswerReceived: game.first_answer_received,
        lastAnswerReceived: game.last_answer_received,
        thumbnailUrl: game.thumbnail_url,
      }
    }),
    summaryData: {
      totalGames: summary.total_games,
      totalPlayed: summary.total_played,
      totalAnswers: summary.total_answers,
      totalExercises: summary.total_exercises,
      totalTeams: summary.total_teams,
      fromDate: summary.from_date,
      untilDate: summary.until_date,
      topGames: summary.top_games.map((game) => {
        return {
          id: game.id,
          gameName: game.name,
          playerInfo: game.player_info,
          modifiedAt: game.updated_at,
          thumbnailUrl: game.thumbnail_url,
        }
      }),
    },
  }
}

export const parsePlayerReportApiData = (data: PlayerReportDataApiResponse): PlayerReportData => {
  const { all_player_accounts, report_data } = data

  return {
    allPlayerAccounts: all_player_accounts,
    reportData: report_data.map((game) => ({
      gameId: game.game_id,
      gameName: game.game_name,
      maxPoints: game.max_points,
      exerciseCount: game.exercise_count,
      playerCount: game.player_count,
      open: game.open,
      coverPictureUrl: game.cover_picture_url,
      mapUrl: game.map_url,
      avgTime: game.avg_time,
      playersCompleted: game.players_completed,
      lastAnswerDate: game.last_answer_date,
      gamerAccounts: game.gamer_accounts.map((player) => ({
        playerEmail: player.player_email,
        teamName: player.team_name,
        teamPoints: player.team_points,
        completedExercises: player.completed_exercises,
        gameCompleted: player.game_completed,
        lastAnswerDate: player.last_answer_date,
        badgeCount: player.badge_count,
      })),
    })),
  }
}
